/* eslint-disable import/order */

import React, { useEffect, useState } from 'react';
import { Col, Input, Modal, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { get, isEqual, set } from 'lodash';
import { Button, CompanyTypeSelect, Form } from '~/components';
import CompanyTypeTitle from '~/layouts/CompanyTypeTitle';
import { CompanyTypeReq } from '~/types/CompanyType';
import { useCompanyTypeContext } from '~/controllers/CompanyTypeContext';
import { useCompanyTypeMutation } from '~/controllers/companyType';
import buildNamePath from '~/utils/buildNamePath';
import IndustrySelect from '~/components/inputs/IndustrySelect';
import BusinessLineSelect from '~/components/inputs/BusinessLineSelect';
import { useHandbook } from '~/controllers/handbook';
import CompanyTypeContentLayout from '~/layouts/CompanyTypeContentLayout';
import colors from '~/constants/colors';
import theme from '~/constants/theme';
import { useGetApplication } from '~/controllers/wizard';
import { useAmendment } from '~/controllers/amendment';
import { handleCompanyType } from '~/utils/handleCompanyType';
import { CompanyTypeBackgroundImg } from '~/img';
import { useIsMobile } from '~/hooks/useScreenSizes';
import paths from '../paths';

const CompanyType: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const companyTypeMutation = useCompanyTypeMutation();
  const context = useCompanyTypeContext();
  const handbook = useHandbook();
  const isMobile = useIsMobile();
  const applicationQuery = useGetApplication();
  const { isAmendment } = useAmendment();
  const businessNature: any = applicationQuery.data?.payload.companyType?.businessNature;
  const businessIndustry: any = applicationQuery.data?.payload.companyType?.businessIndustry;

  const isNewClient = get(applicationQuery.data, 'payload.isNewClient');
  const [businessIndustryDifference, setBusinessIndustryDifference] = useState<boolean>(false);

  const smallAndMediumEnterprise = handbook.data?.handbooks
    ?.find((item) => item.key === 'companyType')
    ?.values?.find((item) => item.key === 'companyType.small_medium_enterprise')?.apiValue;

  useEffect(() => {
    form.setFieldsValue({
      companyType: {
        companyType: smallAndMediumEnterprise,
        businessNature,
        businessIndustry,
      },
    });
  }, [form, smallAndMediumEnterprise, businessNature, businessIndustry]);

  const onValuesChange = (changedValues: CompanyTypeReq, formValues: any) => {
    const formBusinessIndustry: string | undefined = get(changedValues, 'companyType.businessIndustry');
    setBusinessIndustryDifference(!isEqual(businessIndustry, formBusinessIndustry));
    const handbookData = handbook?.data?.handbooks;
    if (!handbookData) return;
    handleCompanyType(form, handbookData, (name) => name)(changedValues, formValues);
  };

  const onFinish = async (values: CompanyTypeReq) => {
    const submit = async (isSynchronized?: boolean | undefined) => {
      try {
        const response = await companyTypeMutation.mutateAsync({
          data: values,
          params: { isSynchronized },
        });
        if (response.stage === 'highRisk') {
          // history.replace(paths.getInTouch._);
          // refer to D2C-1755
          history.replace(paths.letsStart._);
        } else {
          history.replace(paths.letsStart._);
        }
      } catch (error) {
        history.replace(paths.letsStart._); // FIXME handle error!
      }
    };

    if (!isNewClient && businessIndustryDifference) {
      Modal.confirm({
        title: t('Data Modification Confirmation'),
        content: t('Data has been modified. Do you want to save'),
        icon: null,
        centered: true,
        okText: t('Save and Continue'),
        okButtonProps: {
          id: 'companyType_confirmButton',
        },
        cancelText: t('Cancel and Return'),
        cancelButtonProps: { id: 'companyType_cancelButton' },
        onOk: async () => {
          await submit(false);
        },
        onCancel() {},
        width: 450,
      });
    } else if (values.companyType?.companyType === smallAndMediumEnterprise && !isAmendment) {
      Modal.confirm({
        title: t('Confirm'),
        content: t('Please confirm that your company is not an Aggregator or Marketplace'),
        icon: null,
        centered: true,
        okText: t('Agree and Continue'),
        okButtonProps: {
          id: 'companyType_confirmButton',
        },
        cancelText: t('Reselect Company Type'),
        cancelButtonProps: { id: 'companyType_cancelButton' },
        onOk: () => {
          submit() // run async
            .then(() => {})
            .catch(() => {});
        },
        onCancel() {},
        width: 450,
      });
    } else {
      await submit();
    }
  };

  return (
    <StyledCompanyTypeContentLayout
      className="CompanyType"
      header={
        <div className="CompanyType-Header">
          <CompanyTypeTitle text={t('Get started')} subtext={t('On-board Your Business')} />
          {!isMobile && <CompanyTypeBackgroundImg className="CompanyType-MobileImage" />}
          <Typography.Paragraph className="CompanyType-Paragraph">
            {t('Now we know about you, please tell us a little about your business.')}
          </Typography.Paragraph>
        </div>
      }
    >
      <div className="CompanyType-FormSpaceBetween">
        <Form
          layout="vertical"
          form={form}
          onValuesChange={onValuesChange}
          onFinish={onFinish}
          className="CompanyType-Form"
          disabled
        >
          <Form.Item
            name={n(['companyType'])}
            label={t('Company type')}
            required
            rules={[{ required: true }]}
            className="CompanyType-FormItem"
            disabled
            hidden
          >
            <CompanyTypeSelect hint />
          </Form.Item>
          <Form.Item
            name={n(['businessNature'])}
            label={t('Business nature')}
            rules={[{ required: true }]}
            className="CompanyType-FormItem"
            disabled={isAmendment}
          >
            <IndustrySelect hint />
          </Form.Item>
          <Form.ShouldUpdate name={n('businessNature')}>
            {() => {
              const formValues = form.getFieldsValue();
              const businessNature = formValues?.companyType?.businessNature;
              return (
                <Form.Item
                  name={n(['businessIndustry'])}
                  label={t('Business line')}
                  rules={[{ required: true }]}
                  className="CompanyType-FormItem"
                >
                  <BusinessLineSelect businessNature={businessNature} disabled={!businessNature || isAmendment} hint />
                </Form.Item>
              );
            }}
          </Form.ShouldUpdate>
          <Form.Item name={n(['mcc'])} hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col className="CompanyType-ButtonCol">
              <Form.Item noStyle>
                <Button
                  id="companyTypeButton_getStarted"
                  type="primary"
                  htmlType="submit"
                  block
                  disabled={!context.agreed}
                  loading={companyTypeMutation.isLoading}
                >
                  {t('Get started')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </StyledCompanyTypeContentLayout>
  );
};

const StyledCompanyTypeContentLayout = styled(CompanyTypeContentLayout)`
  .CompanyType-Header {
    .CompanyType-MobileImage {
      display: none;
      width: 100%;
      border-radius: 14px;
      margin-bottom: 24px;
    }
    .CompanyType-Paragraph {
      margin-bottom: 32px;
    }
  }
  .CompanyType-FormSpaceBetween {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    .ant-select {
      width: 280px;
    }
    .CompanyType-Form {
      background-color: ${colors.componentBackground};
      padding: 15px;
      border-radius: 20px;
    }
    .CompanyType-ButtonCol {
      width: 90%;
    }
  }
  @media (max-width: ${theme.breakpoints.md}px) {
    .CompanyType-Header {
      margin-bottom: 98px;
    }
  }
  @media (max-width: 650px) {
    .CompanyType-Header {
      margin-bottom: 1em;
      .CompanyType-MobileImage {
        display: flex;
      }
    }
  }
`;

const n = buildNamePath(['companyType']);

export default CompanyType;
